import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  DatePickerContainer,
  Divider,
  Wrapper,
  FilterContainer,
} from './styles'
import { Calendar } from 'react-feather'
import Flatpickr from 'react-flatpickr'
import { subDays } from 'date-fns'
import MultiSelect from '~/components/MultiSelect'
import CheckBox from '~/components/Checkbox'
import { RateShowcase } from '~/components/commons'
import { Button } from 'reactstrap'
import { brazilianStates } from '~/constants/location'
import { DEFAULT_END_SUB_DATE } from '~/constants/calendar'
import { useFormik } from 'formik'
import {
  getFilteredAreaOptionsWithStateAndCity,
  getFilteredCityOptionsWithStateAndArea,
  getFilteredStateOptionsWithCityAndArea,
} from '~/helpers/getFilteredOptions'
import { useFormFilters } from '~/hooks/useFormFilters'
import { useSearchParamsHandler } from '~/contexts/FilterContext'
import { getDefaultFiltersCount } from '~/helpers/getFilterCount'
import { Portuguese } from 'flatpickr/dist/l10n/pt'
import english from 'flatpickr/dist/l10n/default'
import { toast } from 'react-toastify'

function FilterReviewsForm({ onClose }) {
  const { query, setQueries } = useSearchParamsHandler()

  const defaultFilters = {
    states: query.states || [],
    cities: query.cities || [],
    areas: query.areas || [],
    labels: query.labels || [],
    locations: query.locations || [],
    ratings: query.ratings || [],
    content: query.content || [],
    date:
      (query.date?.length && [
        new Date(query.date[0].replace(/-/g, '/')),
        new Date(query.date[1].replace(/-/g, '/')),
      ]) ||
      [],
  }
  const filterCount = getDefaultFiltersCount(defaultFilters)
  const formik = useFormik({
    initialValues: defaultFilters,
    onSubmit: values => {
      if (values.date?.length) {
        const [start, end] = values.date
        values.date = [
          start.toISOString().split('T')[0],
          end.toISOString().split('T')[0],
        ]
      }
      setQueries({ ...values, page: 1 })
      toast(intl.formatMessage({ id: 'Filters added successfully' }), {
        position: 'top-center',
        type: 'success',
      })
      onClose()
    },
  })

  const ratings = formik.values.ratings
  const content = formik.values.content
  const date = formik.values.date

  const intl = useIntl()

  const handleRating = value => {
    const index = ratings.indexOf(value)
    if (index !== -1) {
      formik.setFieldValue(
        'ratings',
        ratings.filter(rating => rating !== value)
      )
    } else {
      formik.setFieldValue('ratings', [...ratings, value])
    }
  }

  const handleWithContent = value => {
    const index = content.indexOf(value)
    if (index !== -1) {
      formik.setFieldValue(
        'content',
        content.filter(item => item !== value)
      )
    } else {
      formik.setFieldValue('content', [...content, value])
    }
  }

  const clearFilter = () => {
    setQueries({
      states: [],
      cities: [],
      areas: [],
      labels: [],
      locations: [],
      ratings: [],
      content: [],
      date: [],
      page: 1,
    })
    onClose()
  }

  const {
    states,
    cities,
    areas,
    labels,
    locations,
    handleFilters,
    filterLoading,
    filters,
  } = useFormFilters({ values: formik.values })

  const withContentLabel = intl.formatMessage({ id: 'With Content' })
  const withoutContentLabel = intl.formatMessage({ id: 'Without Content' })
  return (
    <Wrapper>
      <FilterContainer
        style={{
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        <Calendar size={25} />
        <DatePickerContainer>
          <Flatpickr
            placeholder={intl.formatMessage({ id: 'Select a date' })}
            className="form-control"
            options={{
              mode: 'range',
              defaultDate: date,
              maxDate: subDays(new Date(), DEFAULT_END_SUB_DATE),
              animate: true,
              altInput: true,
              altFormat: 'M d',
              dateFormat: 'Y-m-d',
              locale: intl.locale === 'br' ? Portuguese : english,
            }}
            onChange={value => {
              const [start, end] = value
              if (start && !end) {
                formik.setFieldValue('date', null)
              }
              if (end) {
                formik.setFieldValue('date', value)
              }
            }}
          />
        </DatePickerContainer>
      </FilterContainer>
      <Divider />
      <FilterContainer>
        <h2 className="title">
          <FormattedMessage id="Locations" />
        </h2>
        <div className="content__wrapper">
          <MultiSelect
            uniqueKey="stateFilter"
            value={states}
            onChange={v =>
              handleFilters(v, 'state', val =>
                formik.setFieldValue('states', val)
              )
            }
            placeholder={intl.formatMessage({ id: 'Select a state' })}
            options={filters?.states
              .filter(el =>
                getFilteredStateOptionsWithCityAndArea(el, cities, areas)
              )
              ?.map(state => {
                return {
                  value: [state],
                  label: brazilianStates.find(item => item.uf === state)?.state,
                }
              })}
            loading={filterLoading}
          />
          <MultiSelect
            uniqueKey="cityFilter"
            value={cities}
            onChange={v =>
              handleFilters(v, 'city', val =>
                formik.setFieldValue('cities', val)
              )
            }
            placeholder={intl.formatMessage({ id: 'Select a city' })}
            options={filters?.cities
              ?.filter(item =>
                getFilteredCityOptionsWithStateAndArea(item, states, areas)
              )
              ?.map(city => {
                return {
                  value: [city],
                  label: city,
                }
              })}
            loading={filterLoading}
          />
          <MultiSelect
            uniqueKey="areaFilter"
            value={areas}
            onChange={v =>
              handleFilters(v, 'area', val =>
                formik.setFieldValue('areas', val)
              )
            }
            placeholder={intl.formatMessage({ id: 'Select an area' })}
            options={filters?.areas
              ?.filter(item =>
                getFilteredAreaOptionsWithStateAndCity(item, states, cities)
              )
              ?.map(area => {
                return {
                  value: [area],
                  label: area,
                }
              })}
            loading={filterLoading}
          />
          <MultiSelect
            uniqueKey="locationFilter"
            value={locations}
            onChange={v =>
              handleFilters(v, 'location', val =>
                formik.setFieldValue('locations', val)
              )
            }
            placeholder={intl.formatMessage({ id: 'Select a location' })}
            options={filters?.locations?.map(location => {
              return {
                value: [location.id],
                label: location,
              }
            })}
            renderOption={({ label: location }) => (
              <div className="col">
                <span>{location?.name ?? ''}</span>
                <small style={{ display: 'block', color: '#8D8D8D' }}>
                  {location?.data?.storefrontAddress?.addressLines?.join(
                    ', '
                  ) ?? ''}
                </small>
                <small style={{ display: 'block', color: '#8D8D8D' }}>
                  Store code: {location?.data?.storeCode ?? ''}
                </small>
              </div>
            )}
            loading={filterLoading}
          />
          <MultiSelect
            uniqueKey="labelFilter"
            value={labels}
            onChange={v =>
              handleFilters(v, 'label', val =>
                formik.setFieldValue('labels', val)
              )
            }
            placeholder={intl.formatMessage({ id: 'Select a label' })}
            options={filters?.labels?.map(label => {
              return {
                value: [label],
                label: label,
              }
            })}
            loading={filterLoading}
          />
        </div>
      </FilterContainer>
      <Divider />
      <FilterContainer>
        <h2 className="title">
          <FormattedMessage id="Content" />
        </h2>
        <div className="content__wrapper">
          <CheckBox
            color="primary"
            label={withContentLabel}
            onChange={() => handleWithContent('with')}
            checked={content.includes('with')}
          />
          <CheckBox
            color="primary"
            label={withoutContentLabel}
            onChange={() => handleWithContent('without')}
            checked={content.includes('without')}
          />
        </div>
      </FilterContainer>
      <Divider />
      <FilterContainer>
        <h2 className="title">
          <FormattedMessage id="Rating" />
        </h2>
        <div className="content__wrapper">
          {[1, 2, 3, 4, 5].map(el => (
            <CheckBox
              size={18}
              key={el}
              color="primary"
              label={`star-${el}`}
              render={() => (
                <RateShowcase rating={el} starSize={15} fillColor="#ff9f43" />
              )}
              onChange={() => handleRating(el)}
              checked={ratings.includes(el)}
            />
          ))}
        </div>
      </FilterContainer>
      <div className="modal-footer__variant">
        <Button
          color="primary"
          onClick={() => formik.handleSubmit()}
          style={{ width: '100%' }}
          disabled={!formik.dirty}
        >
          <FormattedMessage id="Add filters" />
        </Button>
        <Button
          color="secondary"
          outline
          onClick={clearFilter}
          style={{ width: '100%' }}
          disabled={filterCount === 0}
        >
          <FormattedMessage id="Clear filter" />
        </Button>
      </div>
    </Wrapper>
  )
}
export default FilterReviewsForm
